import React, { Fragment, useEffect, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { groupBy } from "@repo/utils";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { NavigationItem } from "~/managers/use-navigation-manager";
import { cn } from "~/lib/utils";
import AudioButton from "../ui/audio-button";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const NavItemList = ({
  groupedNavItems,
}: {
  groupedNavItems: Record<string, NavigationItem[]>;
}) => {
  const router = useRouterState();
  const navigate = useNavigate();

  return (
    <ul className="flex flex-col">
      {Object.entries(groupedNavItems).map(([section, items]) => (
        <div key={section} className="border-b px-4 py-6">
          <div className="mb-2 flex space-x-3">
            <span className="w-full text-lg font-semibold text-foreground">
              {section}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {items
              .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
              .map((item) => (
                <div key={item.name}>
                  <button
                    onClick={() => {
                      void navigate({ to: item.href });
                    }}
                    className={cn(
                      "group/item border-default group h-10 flex w-full items-center space-x-3 px-3 py-1 outline-none ring-foreground-muted focus-visible:z-10 focus-visible:ring-1 group-hover:border-foreground-muted dark:hover:bg-bunker-950 hover:bg-gray-200",
                      router.location.pathname === item.href &&
                        "bg-gray-200 dark:bg-bunker-950",
                    )}
                    aria-current={
                      router.location.pathname === item.href ? true : false
                    }
                  >
                    <div
                      className={cn(
                        "w-full truncate text-sm text-foreground-light transition group-hover:text-foreground group-aria-current/item:font-bold group-aria-current/item:text-foreground",
                      )}
                    >
                      <div className="flex w-full items-center justify-between gap-1">
                        <div className="flex w-full items-center gap-2 truncate">
                          <span className="truncate text-base font-normal">
                            {item.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
          </div>
        </div>
      ))}
    </ul>
  );
};

export const SideNav = ({
  navItems,
  title,
}: {
  navItems: NavigationItem[];
  title?: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  const router = useRouterState();

  const { t } = useTranslation("common");

  navItems = navItems.filter((item) => {
    if (item.searchStrings) {
      return item.searchStrings.some((searchString) =>
        searchString.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return true;
  });

  const groupedNavItems = groupBy(navItems, "section");

  useEffect(() => {
    setExpanded(false);
    setSearch("");
  }, [router.location.pathname]);

  return (
    <Fragment>
      <div className="sticky top-0 z-[1000] border-b border-gray-200 bg-white/80 backdrop-blur-sm backdrop-saturate-200 dark:bg-black/50 lg:hidden">
        <AudioButton
          onClick={() => setExpanded(!expanded)}
          type="button"
          className="flex w-full items-center justify-between gap-2 border-b px-4 py-3"
        >
          <div className="flex items-center gap-1">
            <ChevronRightIcon
              className={cn("transition-transform", expanded && "rotate-90")}
            />
            <span>Menu</span>
          </div>
          <span>
            {
              Object.entries(groupedNavItems)
                .map(([_, items]) => items)
                .flat()
                .find((obj) => obj.href === router.location.pathname)?.name
            }
          </span>
        </AudioButton>
        {expanded && (
          <div
            className={cn(
              "absolute h-[calc(100dvh-103px)] overflow-y-scroll w-full flex flex-col bg-white dark:bg-bunker-950",
            )}
          >
            <div className="styled-scrollbar flex flex-col pb-4 pr-2 dark:text-white">
              {Object.entries(groupedNavItems).map(([section, items]) => (
                <div key={section} className="border-b px-4 py-6">
                  <div className="mb-2 flex space-x-3 font-semibold">
                    <span className="w-full text-lg text-foreground">
                      {section}
                    </span>
                  </div>
                  {items
                    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                    .map((item) => (
                      <div key={item.name}>
                        <button
                          onClick={() => {
                            void navigate({ to: item.href });
                          }}
                          className={cn(
                            "group/item border-default group flex w-full items-center space-x-3 px-3 py-1 font-normal outline-none ring-foreground-muted focus-visible:z-10 focus-visible:ring-1 group-hover:border-foreground-muted",
                            router.location.pathname === item.href &&
                              "bg-gray-200 dark:bg-bunker-950",
                          )}
                          aria-current={
                            router.location.pathname === item.href
                              ? true
                              : false
                          }
                        >
                          <div
                            className={cn(
                              "w-full truncate text-sm text-foreground-light transition group-hover:text-foreground group-aria-current/item:font-bold group-aria-current/item:text-foreground",
                            )}
                          >
                            <div className="flex w-full items-center justify-between gap-1">
                              <div className="flex w-full items-center gap-2 truncate">
                                <span className="truncate">{item.name}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="hidden min-w-64 max-w-[32rem] lg:block h-full overflow-hidden">
        <div className="flex h-full w-full flex-col border-r bg-background-secondary">
          {title && (
            <div className="flex max-h-12 min-h-12 items-center border-b px-6">
              <h4 className="text-lg">{title}</h4>
            </div>
          )}
          <div className="flex flex-row border-b">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none border-0 "
              placeholder="Search..."
            />
            {search.length > 0 && (
              <Button
                className="w-10 rounded-none border-0 p-1"
                onClick={() => setSearch("")}
              >
                <CloseIcon className="text-2xl" />
              </Button>
            )}
          </div>

          <div className="flex-grow overflow-y-auto">
            <div className="flex h-full flex-col overflow-y-auto small-scrollbar">
              <nav className="h-full" aria-label="Sidebar" role="menu">
                {navItems.length > 0 && (
                  <NavItemList groupedNavItems={groupedNavItems} />
                )}
                {navItems.length === 0 && (
                  <div className="flex h-full items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <SearchIcon className="text-2xl" />
                      {t("no_results")}
                    </div>
                  </div>
                )}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
